import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "@headlessui/react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { downloadFile } from "../../app/downloadFile";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import FormattedDateAndTime from "../../components/FormattedDateAndTime";
import SimplePagination from "../../components/SimplePagination";
import Select from "../../components/input/Select";
import PersonName, { displayName } from "./PersonName";
import { SecretAuditAction, SecretChangeViewModel } from "./secretsApiSlice";

const pageSize = 5;
const allActions = "all";

const actionLabels: Record<SecretAuditAction, string> = {
	[SecretAuditAction.SecretChanged]: "Secret changed",
	[SecretAuditAction.SecretRestored]: "Secret restored",
	[SecretAuditAction.SecretChangeFailed]: "Change failed",
	[SecretAuditAction.SecretRestoreFailed]: "Restore failed",
	[SecretAuditAction.AccessGranted]: "Access granted",
	[SecretAuditAction.AccessRevoked]: "Access revoked",
	[SecretAuditAction.PasswordCheckFailed]: "Incorrect password",
};

interface Props {
	changes: SecretChangeViewModel[];
	/** The kinds of change this table shows, offered in the filter. */
	filterActions: SecretAuditAction[];
	/** Server path of the full history CSV, for example "/Secrets/SecretHistoryCsv". */
	downloadPath: string;
	/** Used if the server doesn't name the file. */
	downloadFileName: string;
}

/**
 * Recent entries from the secrets audit log, newest first, with a download of the full history. The
 * Secrets and Managers tabs each pass the entries that are about them.
 */
export default function RecentChangesTable({
	changes,
	filterActions,
	downloadPath,
	downloadFileName,
}: Props) {
	const { aspUrl } = useAppSelector((state) => state.site);
	const [page, setPage] = useState(0);
	const [actionFilter, setActionFilter] = useState<string>(allActions);
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState("");

	const filterOptions = useMemo(
		() => [
			{ value: allActions, label: "All" },
			...filterActions.map((action) => ({ value: action, label: actionLabels[action] })),
		],
		[filterActions]
	);

	const filteredChanges = useMemo(
		() =>
			actionFilter === allActions
				? changes
				: changes.filter((change) => change.action === actionFilter),
		[changes, actionFilter]
	);

	const pagedChanges = useMemo(
		() => filteredChanges.slice(page * pageSize, (page + 1) * pageSize),
		[filteredChanges, page]
	);

	function handleFilterChange(value: string | number | undefined) {
		setActionFilter(value === undefined ? allActions : String(value));
		setPage(0);
	}

	async function handleDownload() {
		setDownloadError("");
		setIsDownloading(true);
		try {
			const result = await downloadFile(`${aspUrl}${downloadPath}`, downloadFileName);
			if (!result.ok) {
				setDownloadError(result.error ?? "Something went wrong downloading the file.");
			}
		} finally {
			setIsDownloading(false);
		}
	}

	return (
		<>
			<h2 className="text-s sm:text-left pt-8">Recent History</h2>
			<div className="flex gap-3 mt-4 items-end justify-between">
				<Select
					simpleListBoxId={`filter-${downloadFileName}`}
					label="Action"
					value={actionFilter}
					setValue={handleFilterChange}
					options={filterOptions}
					fieldClasses="w-64"
					simpleListBoxClasses="w-full"
				/>
				<Button
					id={`download-${downloadFileName}-button`}
					className="btn btn-neutral h-fit w-fit whitespace-nowrap"
					disabled={isDownloading}
					onClick={handleDownload}>
					<span className="flex items-center">
						<ArrowDownTrayIcon className="size-4 mr-2" aria-hidden="true" />
						{isDownloading ? "Downloading..." : "Download Full History"}
					</span>
				</Button>
			</div>
			<AlertMessage
				className="mt-2"
				type={AlertLevel.Error}
				message={downloadError}
				showIcon
				onClear={() => setDownloadError("")}
			/>
			<DataTable
				value={pagedChanges}
				dataKey="id"
				emptyMessage={
					actionFilter === allActions ? "No changes yet" : "No matching changes"
				}
				stripedRows
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ paddingTop: "16px" }}>
				<Column
					header="When"
					body={(change: SecretChangeViewModel) => (
						<FormattedDateAndTime date={change.changedAtUtc} />
					)}
				/>
				<Column
					header="Who"
					body={(change: SecretChangeViewModel) => (
						<PersonName
							userName={change.changedBy}
							fullName={change.changedByFullName}
						/>
					)}
				/>
				<Column
					header="What"
					body={(change: SecretChangeViewModel) => describeChange(change)}
				/>
				<Column header="IP Address" field="ipAddress" />
			</DataTable>
			<SimplePagination
				itemCount={filteredChanges.length}
				page={page}
				pageSize={pageSize}
				totalCount={changes.length}
				isFiltered={actionFilter !== allActions}
				onChange={setPage}
				className="border-none"
			/>
		</>
	);
}

function describeChange(change: SecretChangeViewModel): string {
	const target = displayName(change.targetUser, change.targetUserFullName);

	switch (change.action) {
		case SecretAuditAction.SecretChanged:
			return `Changed ${change.secretName}`;
		case SecretAuditAction.SecretRestored:
			return `Restored the previous ${change.secretName}`;
		case SecretAuditAction.SecretChangeFailed:
			return `Couldn't change ${change.secretName}: Key Vault refused`;
		case SecretAuditAction.SecretRestoreFailed:
			return `Couldn't restore the previous ${change.secretName}`;
		case SecretAuditAction.AccessGranted:
			return `Granted ${target} access`;
		case SecretAuditAction.AccessRevoked:
			return `Revoked ${target}'s access`;
		case SecretAuditAction.PasswordCheckFailed:
			if (change.secretName) {
				return `Incorrect password while changing ${change.secretName}`;
			}
			if (change.targetUser) {
				return `Incorrect password while changing ${target}'s access`;
			}
			return "Incorrect password";
		default:
			return String(change.action);
	}
}
